import type { IAggFuncParams } from 'ag-grid-community';
import Big from 'big.js';
import type { SizeValue } from './types';

const EMPTY_NO_AGG_RESULT: SizeValue = { value: '', currency: '', aggregate: false };
const EMPTY_AGGREGATABLE_RESULT: SizeValue = { value: '', currency: '', aggregate: true };

export type UniformSizeAggFuncParams = Pick<IAggFuncParams<unknown, SizeValue>, 'values'>;

function isPopulated(size: SizeValue | null | undefined): size is SizeValue {
  return size != null && size.value != null && size.value !== '';
}

function sizeValuesMatch(first: SizeValue, next: SizeValue): boolean {
  if (first.currency !== next.currency) {
    return false;
  }

  try {
    // Big throws synchronously for malformed numeric values, so treat those as non-uniform.
    return Big(first.value).eq(next.value);
  } catch {
    return false;
  }
}

/**
 * Propagates a SizeValue to the group row only when all populated child values are equal and in the same currency.
 *
 * Example: account-level margin appears on every child position row, so a grouped row can show
 * `100 USD` only when each populated child also has `100 USD`; mixed values/currencies stay blank.
 */
export function uniformSizeAggFunc({ values }: UniformSizeAggFuncParams): SizeValue {
  if (values.some(value => value?.aggregate === false)) {
    return EMPTY_NO_AGG_RESULT;
  }

  const populatedValues = values.filter(isPopulated);
  if (!populatedValues.length) {
    return EMPTY_AGGREGATABLE_RESULT;
  }

  // Populated amounts need a currency; blank amounts are filtered out above.
  if (populatedValues.some(size => size.currency == null || size.currency === '')) {
    return EMPTY_NO_AGG_RESULT;
  }

  const [firstValue, ...rest] = populatedValues;
  return rest.every(value => sizeValuesMatch(firstValue, value)) ? firstValue : EMPTY_NO_AGG_RESULT;
}
